'use client'

import { Download } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'

import { cn } from '@/lib/cn'
import { mimeTypeToExtensions } from '@/utils/mimeTypeToExtension'

import { Button } from '../button'

type DocViewerProps = {
  fileUrl: string
  fileName: string
  mimeType: string
  width?: number
  height?: number
  className?: string
  sizes?: string
}

const handleDownload = (url: string) => {
  window.open(url, '_blank', 'noopener,noreferrer')
}

export function DocViewer({
  fileUrl,
  fileName,
  mimeType,
  width,
  height,
  className,
  sizes = '(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw',
}: DocViewerProps) {
  const isImage = mimeType?.startsWith('image/') ?? false

  if (isImage) {
    if (width && height) {
      return (
        <div className={cn('group relative w-full', className)}>
          <div className="relative overflow-hidden rounded-lg">
            <Image
              src={fileUrl}
              alt={fileName}
              width={width}
              height={height}
              className="object-contain transition-all duration-300 group-hover:blur-sm"
              sizes={sizes}
            />
          </div>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="absolute right-2 top-2 z-10 opacity-0 transition-opacity group-hover:opacity-100"
            onClick={() => handleDownload(fileUrl)}
          >
            <Download className="size-4" />
          </Button>
        </div>
      )
    }

    return (
      <div
        className={cn(
          'group relative w-full overflow-hidden rounded-lg',
          className || 'h-[200px]',
        )}
      >
        <Image
          src={fileUrl}
          alt={fileName}
          fill
          className="rounded-md object-contain transition-all duration-300 group-hover:blur-sm"
          sizes={sizes}
        />
        <div className="absolute inset-0 z-10 flex items-center justify-center opacity-0 transition-opacity group-hover:opacity-100">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => handleDownload(fileUrl)}
          >
            <Download className="size-4" />
          </Button>
        </div>
      </div>
    )
  }

  const fileExtension = mimeTypeToExtensions([mimeType])[0]

  return (
    <div className="flex items-center gap-2">
      <Button variant="link" size="link" asChild>
        <Link href={fileUrl} target="_blank" rel="noreferrer">
          Visualizar o arquivo {fileExtension}
        </Link>
      </Button>
    </div>
  )
}
